/**
 * OPTIONAL biometric app-lock (Phase 3) — persistence + biometric bridge + the
 * lock state machine.
 *
 * ## CRITICAL: this is the OPT-IN, OFF-BY-DEFAULT deliverable
 * It is completely separate from the mandatory, invisible secure-token storage
 * (Phases 1–2). When the setting is OFF (the default) — OR the runtime isn't
 * native, OR biometrics aren't available — the machine NEVER leaves `unlocked`:
 * no overlay, no prompt, zero effect on launch or resume. Nobody who hasn't
 * opted in is ever gated, and individual requests are NEVER gated.
 *
 * The biometric prompt is **Tauri-mobile only** (Android BiometricPrompt / iOS
 * LocalAuthentication via `tauri-plugin-biometric`). Desktop Tauri and plain web
 * have no biometric plugin → treated as "unavailable" → always unlocked.
 *
 * The setting is persisted **per-device** in `localStorage` — it is deliberately
 * NOT a synced account preference (locking is a property of THIS device).
 */

import { isTauriRuntime } from "@/lib/platform";

/**
 * Per-DEVICE localStorage key for the opt-in app-lock. NOT synced to the account.
 * Absent / any value other than `"1"` means OFF — the default for everyone.
 */
const APP_LOCK_ENABLED_KEY = "apunte-digital.app-lock-enabled";

/** Read the per-device app-lock setting. Default **OFF** (`false`) for everyone. */
export function isAppLockEnabled(): boolean {
  try {
    return globalThis.localStorage.getItem(APP_LOCK_ENABLED_KEY) === "1";
  } catch {
    // Storage unavailable (private mode / sandbox) — treat as OFF.
    return false;
  }
}

/** Persist the per-device app-lock setting. */
export function setAppLockEnabled(enabled: boolean): void {
  try {
    if (enabled) {
      globalThis.localStorage.setItem(APP_LOCK_ENABLED_KEY, "1");
    } else {
      globalThis.localStorage.removeItem(APP_LOCK_ENABLED_KEY);
    }
  } catch {
    // Storage unavailable — silently no-op; the setting stays at its default OFF.
  }
}

/** Dynamic import so the web bundle never statically pulls in the Tauri API. */
async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

/** Shape of `plugin:biometric|status` (mirrors `@tauri-apps/plugin-biometric`). */
interface BiometricStatus {
  isAvailable: boolean;
  biometryType?: number;
  error?: string;
}

/**
 * Whether a biometric / device-credential prompt is usable RIGHT NOW. `false` on
 * web and on desktop Tauri (the biometric plugin is mobile-only), and `false` if
 * the device has no enrolled biometrics. Mirrors `checkStatus()` from
 * `@tauri-apps/plugin-biometric`, invoked directly (via `plugin:biometric|status`)
 * so the web bundle stays free of a static Tauri import.
 */
export async function checkBiometricAvailable(): Promise<boolean> {
  if (!isTauriRuntime()) {
    return false;
  }
  try {
    const status = await tauriInvoke<BiometricStatus>("plugin:biometric|status");
    return status.isAvailable;
  } catch {
    // No biometric plugin registered (desktop) or the probe errored → unavailable.
    return false;
  }
}

/**
 * Prompt for biometrics / device credential. Resolves `true` on success, `false`
 * on failure / cancel / unavailable. Mirrors `authenticate(reason, options)` from
 * `@tauri-apps/plugin-biometric` (`plugin:biometric|authenticate`).
 */
export async function authenticateBiometric(reason: string): Promise<boolean> {
  if (!isTauriRuntime()) {
    return false;
  }
  try {
    await tauriInvoke("plugin:biometric|authenticate", {
      reason,
      // Allow the device PIN / pattern / passcode as a fallback so a user whose
      // biometric read fails can still get in without being locked out.
      allowDeviceCredential: true,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Is the app-lock ARMED for this launch? Only when the runtime is native AND the
 * user opted in. This single gate is what guarantees "OFF by default = zero
 * effect": when it returns `false` the machine stays `unlocked` and never touches
 * biometrics — the default path for everyone.
 */
export function isAppLockArmed(): boolean {
  return isTauriRuntime() && isAppLockEnabled();
}

export type AppLockState = "unlocked" | "locked" | "authenticating";

interface AppLockMachineDeps {
  /** Whether the lock is armed (native + opted-in). */
  isArmed: () => boolean;
  /** Whether a biometric prompt is available right now. */
  checkAvailable: () => Promise<boolean>;
  /** Run the biometric prompt; resolves `true` on success. */
  authenticate: () => Promise<boolean>;
}

interface AppLockMachine {
  /** Current state (source of truth; mirror it into a Solid signal via `onChange`). */
  state: () => AppLockState;
  /** Evaluate on launch / resume-to-foreground: lock + prompt when armed. */
  enforce: () => Promise<void>;
  /** Re-run the prompt after a failure / cancel (the Retry button). */
  retry: () => Promise<void>;
}

/**
 * Framework-agnostic lock state machine (unit-testable without Solid). `onChange`
 * is invoked on every transition so a Solid signal can mirror `state`.
 *
 * States: `unlocked` (children visible) ⇄ `locked`/`authenticating` (overlay).
 * The ONLY path into `locked` is `enforce()`/`retry()` while `isArmed()` is true
 * AND biometrics are available; otherwise it snaps to `unlocked`. So the OFF /
 * not-native / unavailable paths are pure pass-through (fail-open — a user is
 * never locked out by a device that can't prompt).
 */
export function createAppLockMachine(
  deps: AppLockMachineDeps,
  onChange?: (state: AppLockState) => void,
): AppLockMachine {
  // Initial state: `locked` when armed (so there's no flash of app content before
  // the prompt), otherwise `unlocked` (the default path for everyone).
  let current: AppLockState = deps.isArmed() ? "locked" : "unlocked";

  const set = (next: AppLockState): void => {
    if (next === current) {
      return;
    }
    current = next;
    onChange?.(next);
  };

  const attempt = async (): Promise<void> => {
    set("authenticating");
    const ok = await deps.authenticate();
    set(ok ? "unlocked" : "locked");
  };

  const enforce = async (): Promise<void> => {
    if (!deps.isArmed()) {
      // OFF / not native → pass-through. Never prompts, never overlays.
      set("unlocked");
      return;
    }
    // Already prompting — don't stack a second prompt (e.g. focus + visibility
    // firing together).
    if (current === "authenticating") {
      return;
    }
    const available = await deps.checkAvailable();
    if (!available) {
      // Fail-OPEN: never lock a user out when the device can't prompt.
      set("unlocked");
      return;
    }
    set("locked");
    await attempt();
  };

  const retry = async (): Promise<void> => {
    if (!deps.isArmed()) {
      set("unlocked");
      return;
    }
    if (current === "authenticating") {
      return;
    }
    await attempt();
  };

  return {
    state: () => current,
    enforce,
    retry,
  };
}
